/* Authored here and in --gcc-ease; grow-card-carousel.spec.tsx pins the two together. */
export const BEZIER = [0.32, 0.72, 0, 1] as const;

/* cubic-bezier(x1,y1,x2,y2) solved for y at x, the way the browser solves a
   CSS timing function: Newton-Raphson, clamped. */
export function easeGlide(x: number): number {
  const [x1, y1, x2, y2] = BEZIER;
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const sampleX = (t: number) => ((ax * t + bx) * t + cx) * t;
  const slopeX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;

  let t = x;
  for (let i = 0; i < 8; i += 1) {
    const error = sampleX(t) - x;
    if (Math.abs(error) < 1e-5) {
      break;
    }
    const slope = slopeX(t);
    if (Math.abs(slope) < 1e-6) {
      break;
    }
    t -= error / slope;
  }
  t = Math.min(1, Math.max(0, t));
  return ((ay * t + by) * t + cy) * t;
}

/* Rects rather than offsetLeft: offsetLeft is relative to the nearest positioned
   ancestor, which is not necessarily the scroller. */
export function distanceFromCentre(viewport: HTMLDivElement, slot: HTMLDivElement): number {
  const viewportRect = viewport.getBoundingClientRect();
  const slotRect = slot.getBoundingClientRect();
  return slotRect.left + slotRect.width / 2 - (viewportRect.left + viewportRect.width / 2);
}

/* Measured once and scroll-invariant, so it survives a grow. Only a re-pitch
   (resize, breakpoint, re-authored list) invalidates it. */
export type RailMetrics = {
  base: number;
  maxScroll: number;
  pitch: number;
};

/* Every read before any write, each rect once — a read-after-write forces an
   extra layout pass. */
export function measureRail(
  viewport: HTMLDivElement,
  slots: (HTMLDivElement | null)[]
): RailMetrics | null {
  const first = slots[0];
  const second = slots[1];
  if (!first || !second) {
    return null;
  }

  const viewportRect = viewport.getBoundingClientRect();
  const firstRect = first.getBoundingClientRect();
  const pitch = second.getBoundingClientRect().left - firstRect.left;
  if (!pitch) {
    return null;
  }

  const centre = viewportRect.left + viewportRect.width / 2;

  return {
    base: firstRect.left + firstRect.width / 2 - centre + viewport.scrollLeft,
    maxScroll: Math.max(0, viewport.scrollWidth - viewport.clientWidth),
    pitch
  };
}

/** Where slot `index` has to sit for the rail to be centred on it. */
export function seekTarget(rail: RailMetrics, index: number): number {
  return Math.max(0, Math.min(rail.base + index * rail.pitch, rail.maxScroll));
}

/* The same answer, measured — fallback for a rail with no pitch (one card,
   or no layout yet). */
export function measuredSeekTarget(viewport: HTMLDivElement, slot: HTMLDivElement): number {
  return Math.max(
    0,
    Math.min(
      viewport.scrollLeft + distanceFromCentre(viewport, slot),
      viewport.scrollWidth - viewport.clientWidth
    )
  );
}

/* The slot nearest the grow position. Takes the metrics rather than the cache
   holding them, so a caller can see whether it is about to spend a measurement. */
export function nearestSlot(
  viewport: HTMLDivElement,
  slots: (HTMLDivElement | null)[],
  rail: RailMetrics | null
): number {
  if (rail) {
    /* ceil(x - 0.5) rather than round, so an exact tie takes the LOWER index —
       which is what the loop below's strict `<` does. */
    const index = Math.ceil((viewport.scrollLeft - rail.base) / rail.pitch - 0.5);
    return Math.max(0, Math.min(slots.length - 1, index));
  }

  let nearest = 0;
  let nearestDistance = Number.POSITIVE_INFINITY;
  slots.forEach((slot, index) => {
    if (!slot) {
      return;
    }
    const distance = Math.abs(distanceFromCentre(viewport, slot));
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearest = index;
    }
  });
  return nearest;
}

/* Slot under a horizontal position, in viewport coordinates — read off the
   CARD inside the slot, not off the slot itself. From 1280 up the slot is a
   565px pitch that neither card fills: the grown card overhangs it by 40px each
   side, and every other card is centred in its slot and then pushed 55.5px
   OUTWARD, away from the grown one, to make room for that overhang
   (WAT-12630). Slot box and painted card therefore diverge, and a slot read
   answers a click on a card's outer edge with its NEIGHBOUR, or a click in the
   grown card's overhang with the slot it overhangs into. The card is the slot's
   only element child. A slot with no child yet is skipped, like a shed ref —
   there is nothing painted there to hit — and a point in the gap between two
   cards belongs to neither, which is still the null the caller treats as
   "no card". */
export function slotAt(slots: (HTMLDivElement | null)[], clientX: number): number | null {
  for (let index = 0; index < slots.length; index += 1) {
    const card = slots[index]?.firstElementChild;
    if (!card) {
      continue;
    }
    const box = card.getBoundingClientRect();
    if (clientX >= box.left && clientX <= box.right) {
      return index;
    }
  }

  return null;
}
